import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter, matchPath } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListSubheader from '@mui/material/ListSubheader';
import Badge from '@mui/material/Badge';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import { openDrawer, closeDrawer, loadHomeData } from './actions';
import {
  getDrawerOpen,
  getEventItems,
  getLastSeenEventTime,
  getSelectedOrganization,
  getUserInfo,
} from './selectors';
import routes from './routes';
import { useWindowSize } from './hooks';
import { RoleContainer } from './roles';

const drawerWidth = 220;

function DrawerListItem({
  routes,
  subroutes,
  route,
  location,
  onClick = () => {},
  toggleDrawer,
  nested,
  secondaryIcon,
  badge = 0,
  additionalPaths = [],
}) {
  const size = useWindowSize();
  const theme = useTheme();
  const mobile = size.width < theme.breakpoints.values.sm;
  let initOpen = false;
  let localStorageOrgId;
  if (subroutes) {
    localStorageOrgId = `sswm.subRouteOpen${route.path}`;
    initOpen = localStorage.getItem(localStorageOrgId);
  }

  const [open, setOpen] = useState(initOpen === 'true');
  const handleClick = (e) => {
    e.preventDefault();
    localStorage.setItem(localStorageOrgId, !open);
    setOpen(!open);
  };
  let expander = null;
  if (subroutes) {
    expander = open ? (
      <ExpandLess onClick={handleClick} />
    ) : (
      <ExpandMore onClick={handleClick} />
    );
  }
  let isSelected = !!matchPath(location.pathname, {
    path: route.path,
    exact: true,
  });

  if (!isSelected) {
    isSelected = additionalPaths.some(
      (p) => !!matchPath(location.pathname, { path: p })
    );
  }

  if (subroutes) {
    subroutes.forEach((route) => {
      const match = matchPath(location.pathname, {
        path: route.path,
      });
      if (match) {
        isSelected = true;
      }
    });
  }

  let _onClick = mobile
    ? () => {
        onClick();
        toggleDrawer();
      }
    : onClick;

  let sections;
  if (subroutes && subroutes.length > 0) {
    sections = subroutes
      .map((route) => route.section)
      .filter((v, i, a) => v && a.indexOf(v) === i);

    if (sections.length) {
      sections = sections.map((section) => ({
        subheader: section,
        routes: subroutes.filter((route) => route.section === section),
      }));
    } else {
      sections = [
        {
          subheader: null,
          routes: subroutes,
        },
      ];
    }
  }
  const isExternalLink = route.path.startsWith('http');
  return (
    <>
      <ListItem
        button
        component={isExternalLink ? 'a' : RouterLink}
        target={isExternalLink ? '_blank' : undefined}
        rel={isExternalLink ? 'noopener noreferrer' : undefined}
        href={isExternalLink ? route.path : undefined}
        to={route.path}
        onClick={subroutes && subroutes.length > 0 ? null : _onClick}
        sx={(theme) => ({
          borderRadius: '5px',
          paddingTop: '3px',
          paddingBottom: '3px',
          marginTop: theme.spacing(1),
          marginBottom: theme.spacing(1),
          backgroundColor: isSelected
            ? theme.palette.application.grayblue[100]
            : 'transparent',
          color: isSelected
            ? theme.palette.application.grayblue[900]
            : theme.palette.application.grayblue[700],
        })}
      >
        <ListItemText
          primary={route.title}
          sx={{
            '& .MuiListItemText-primary': {
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              fontWeight: 500,
            },
          }}
        />
        {badge > 0 && (
          <Badge
            style={{ marginRight: 11 }}
            badgeContent={badge}
            color="secondary"
          ></Badge>
        )}
        {expander}
        {secondaryIcon}
      </ListItem>
      {subroutes && subroutes.length > 0 && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {sections.map((section) => {
              return [
                section.subheader && (
                  <ListSubheader key={section.subheader}>
                    {section.subheader}
                  </ListSubheader>
                ),
                section.routes.map((subroute) => (
                  <ListItem
                    key={subroute.path}
                    button
                    sx={{
                      paddingLeft: theme.spacing(4),
                      paddingTop: theme.spacing(0.5),
                      paddingBottom: theme.spacing(0.5),
                    }}
                    component={RouterLink}
                    to={subroute.path}
                    onClick={onClick}
                  >
                    <ListItemText
                      inset
                      primary={subroute.title}
                      sx={{
                        padding: 0,
                        '& .MuiListItemText-primary': {
                          fontSize: 13,
                          wordWrap: 'break-word',
                        },
                      }}
                    />
                  </ListItem>
                )),
              ];
            })}
          </List>
        </Collapse>
      )}
    </>
  );
}

function DrawerMenu(props) {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { window, ponds, sites } = props;
  const open = useSelector(getDrawerOpen);
  const user = useSelector(getUserInfo);
  const events = useSelector(getEventItems);
  const lastSeenEventTime = useSelector(getLastSeenEventTime);
  const organization = useSelector(getSelectedOrganization);

  let unseenEventCount = events.length;

  if (lastSeenEventTime) {
    unseenEventCount = events.filter(
      (event) => new Date(event.time) > new Date(lastSeenEventTime)
    ).length;
  }

  const toggleDrawer = () => {
    if (open) {
      dispatch(closeDrawer());
    } else {
      dispatch(openDrawer());
    }
  };

  const refresh = () => {
    if (organization) {
      dispatch(loadHomeData(organization));
    }
  };

  const menu = (
    <div>
      <RouterLink to="/">
        <img
          src="/SmartSWM-E Final.png"
          alt="SmartSWM"
          height="50"
          style={{ paddingLeft: 8, paddingTop: 8 }}
        />
      </RouterLink>
      <List component="nav" aria-label="main" sx={{ paddingTop: 0 }}>
        <DrawerListItem
          {...props}
          route={routes.find((r) => r.title === 'System')}
          toggleDrawer={toggleDrawer}
          onClick={refresh}
        />
        <DrawerListItem
          {...props}
          route={routes.find((r) => r.title === 'Sites')}
          toggleDrawer={toggleDrawer}
          additionalPaths={['/facility/:facilityId']}
        />
        <DrawerListItem
          {...props}
          route={routes.find((r) => r.title === 'Events')}
          toggleDrawer={toggleDrawer}
        />
        <DrawerListItem
          {...props}
          route={routes.find((r) => r.title === 'Timeline')}
          toggleDrawer={toggleDrawer}
          badge={unseenEventCount}
        />
        <DrawerListItem
          {...props}
          route={routes.find((r) => r.title === 'Map')}
          toggleDrawer={toggleDrawer}
        />
        <DrawerListItem
          {...props}
          route={routes.find((r) => r.title === 'Network')}
          toggleDrawer={toggleDrawer}
        />
        <DrawerListItem
          {...props}
          route={routes.find((r) => r.title === 'Simulation')}
          toggleDrawer={toggleDrawer}
        />
        <DrawerListItem
          {...props}
          route={routes.find((r) => r.title === 'Reports')}
          toggleDrawer={toggleDrawer}
        />
        <DrawerListItem
          {...props}
          route={routes.find((r) => r.title === 'Help')}
          toggleDrawer={toggleDrawer}
        />
      </List>
      <Divider />
      <List>
        {!!user && (
          <DrawerListItem
            {...props}
            route={{
              path: `/user/${user.user_id?.replace('auth0|', '')}`,
              title: user.email,
            }}
            toggleDrawer={toggleDrawer}
          />
        )}
        {!!organization && (
          <DrawerListItem
            {...props}
            route={{
              path: `/organization/${organization.org_id}`,
              title: organization.org_nm,
            }}
            toggleDrawer={toggleDrawer}
          />
        )}
        <RoleContainer role={'orgadmin'}>
          <DrawerListItem
            {...props}
            route={routes.find((r) => r.title === 'Administrator')}
            toggleDrawer={toggleDrawer}
            subroutes={routes.find((r) => r.title === 'Administrator').routes}
          />
        </RoleContainer>
        <DrawerListItem
          {...props}
          route={{
            path: 'https://smartswm.com',
            title: 'smartswm.com',
          }}
          toggleDrawer={toggleDrawer}
        />
      </List>
    </div>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box
      aria-label="sswm menu"
      component={'nav'}
      sx={(theme) => ({
        [theme.breakpoints.up('sm')]: {
          width: drawerWidth,
          flexShrink: 0,
        },
      })}
    >
      <Drawer
        sx={(theme) => ({
          display: { sm: 'none', xs: 'block' },
          '& .MuiDrawer-paper': {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            padding: theme.spacing(1),
          },
        })}
        container={container}
        variant="temporary"
        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
        open={open}
        onClose={toggleDrawer}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
      >
        {menu}
      </Drawer>
      <Drawer
        sx={(theme) => ({
          display: { sm: 'block', xs: 'none' },
          '& .MuiDrawer-paper': {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            padding: theme.spacing(1),
            width: drawerWidth,
            flexShrink: 0,
          },
        })}
        variant="permanent"
        open
      >
        {menu}
      </Drawer>
    </Box>
  );
}
export default withRouter(DrawerMenu);
